import { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  getStoredAccounts,
  switchAccount as switchAccountUtil,
  removeAccount as removeAccountUtil,
  addAccount,
  StoredAccount,
  getCurrentUser,
} from '../util/auth';
import { useGetUser } from './api/user';

export function useAccounts() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<StoredAccount[]>(getStoredAccounts);

  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.sub ? Number(currentUser.sub) : undefined;

  const { data: userData } = useGetUser(currentUserId);

  // Migrate existing token to accounts if needed
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedAccounts = getStoredAccounts();

    if (token && userData && storedAccounts.length === 0) {
      addAccount(token, {
        id: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        image: userData.image,
      });
      setAccounts(getStoredAccounts());
    }
  }, [userData]);

  const refreshAccounts = useCallback(() => {
    setAccounts(getStoredAccounts());
  }, []);

  const switchAccount = useCallback(
    (id: number) => {
      const success = switchAccountUtil(id);
      if (success) {
        queryClient.clear();
        refreshAccounts();
        navigate('/home');
      }
      return success;
    },
    [queryClient, navigate, refreshAccounts],
  );

  const removeAccount = useCallback(
    (id: number) => {
      removeAccountUtil(id);
      refreshAccounts();
    },
    [refreshAccounts],
  );

  return {
    accounts,
    currentUserId,
    switchAccount,
    removeAccount,
    refreshAccounts,
  };
}
